import renderInlineItalic from 'lib/utils/renderInlineItalic';
import Image from 'components/shared/image/image';
import RichText from 'components/shared/richtext';
import VideoPlayer from 'components/shared/video/video';
import classNames from 'classnames';
import Button from 'components/shared/button/button';
import Icon from 'components/shared/icon';
import { useState, useEffect, useRef } from 'react';
import styles from './taggedProducts.module.scss';
import ProductCard from '../productCard';

const ProductPopup = ({ product, setProduct, locale }) => {
  const popupRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (product) {
      // Store the previously focused element
      previousFocusRef.current = document.activeElement;

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = '';
        // Restore focus to the previously focused element
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
        }
      };
    }
    return undefined;
  }, [product]);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (product && popupRef.current) {
      // Focus the popup container
      popupRef.current.focus();

      // Handle escape key
      const handleEscape = e => {
        if (e.key === 'Escape') {
          setProduct(null);
        }
      };

      // Trap focus within the popup
      const handleTabKey = e => {
        if (e.key === 'Tab') {
          const focusableElements = popupRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      };

      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTabKey);

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [product, setProduct]);

  if (!product) return null;

  return (
    <div
      className={styles.overlay}
      onClick={e => {
        if (e.target === e.currentTarget) {
          setProduct(null);
        }
      }}
    >
      <div
        ref={popupRef}
        className={styles.container}
        aria-modal="true"
        role="dialog"
        aria-labelledby="popup-title"
        aria-describedby="popup-description"
        tabIndex={-1}
      >
        <div className={styles.popupContent}>
          <ProductCard data={{ product }} locale={locale} isPopup />
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={() => setProduct(null)}
          aria-label={`Close ${product.title} product details`}
        >
          <Icon type="close" className={styles.closeIcon} fill="white" />
        </button>
      </div>
    </div>
  );
};

const TaggedProducts = ({ data, locale }) => {
  const { title, titleFont, body, button, media, mediaPosition, productTags, anchorId } = data;
  const titleClass = classNames(styles.title, titleFont !== 'Acumin Pro' && styles.octave);
  const filteredTags = productTags.filter(tag => tag?.product);

  const [product, setProduct] = useState(null);
  const mediaRef = useRef(null);

  // Generate unique IDs for accessibility
  const sectionId = `tagged-products-${Math.random().toString(36).substr(2, 9)}`;
  const mediaId = `media-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <section id={anchorId} aria-labelledby={title ? `${sectionId}-title` : undefined}>
      <div className={classNames(styles.wrapper, { [styles.reversed]: mediaPosition === 'Right' })}>
        <div className={styles.mediaContainer} ref={mediaRef}>
          {media.type === 'Image' && (
            <Image
              id={mediaId}
              className={styles.media}
              src={media.image}
              loading="lazy"
              layout="responsive"
              sizes="(max-width: 899px) 100vw, 50vw"
              alt={media.alt || 'Product showcase image'}
            />
          )}
          {media.type === 'Video' && media.video && (
            <VideoPlayer
              id={mediaId}
              className={classNames(styles.media)}
              thumbnail={media.thumbnail}
              source={media.video}
              controlsVersion={2}
              autoPlay
              aspectRatio={media.videoAspectRatio}
              objectFit={media.objectFit}
              aria-label={media.alt || 'Product showcase video'}
            />
          )}

          {/* Product tags with improved accessibility */}
          {filteredTags.length > 0 && (
            <div
              role="group"
              aria-label="Interactive product tags"
              aria-describedby={`${sectionId}-tags-description`}
            >
              {filteredTags.map((tag, index) => (
                <button
                  type="button"
                  key={`productTag_${tag.sys.id}`}
                  aria-label={`View details for ${tag?.product?.title}${
                    tag.product.price ? ` - ${tag.product.price}` : ''
                  }`}
                  aria-describedby={`tag-${tag.sys.id}-description`}
                  className={styles.productTag}
                  style={{
                    '--top-mobile': `${tag.topMobile}%`,
                    '--top-desktop': `${tag.topDesktop}%`,
                    '--left-mobile': `${tag.leftMobile}%`,
                    '--left-desktop': `${tag.leftDesktop}%`,
                  }}
                  onClick={() => setProduct(tag.product)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setProduct(tag.product);
                    }
                  }}
                >
                  <Icon
                    type="tag"
                    className={styles.tagIcon}
                    fill={tag.color.toLowerCase()}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={classNames(styles.content)}>
          {title && (
            <h2 id={`${sectionId}-title`} className={titleClass}>
              {renderInlineItalic(title)}
            </h2>
          )}
          {body?.json && (
            <RichText
              className={classNames(styles.text)}
              text={body.json}
              aria-describedby={title ? `${sectionId}-title` : undefined}
            />
          )}
          {button && (
            <Button className={classNames(styles.button)} locale={locale} button={button} />
          )}
        </div>
      </div>

      <ProductPopup product={product} setProduct={setProduct} locale={locale} />
    </section>
  );
};

export default TaggedProducts;
